import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { RosterFilterBarButton, RosterFilterMenu } from './';
import { ReactComponent as DownArrowSvg } from 'common/images/down-arrow.svg';
import BEM from 'common/bem';
import propTypes from 'common/propTypes';
import { UnreadBadgeCount } from 'common/components';
import { mobxInjectSelect } from 'common/utils';
import { ContextMenu } from 'common/components/ContextMenu';
import { useAppSelector } from 'redux-stores';
import useFilterTasks from 'common/hooks/useFilterTasks';

const classes = BEM.with('RosterFilterBar');

const MENU_OFFSET_X = -6;
const MENU_OFFSET_Y = 3;
const NUM_BUTTONS = 2;

const TasksTabButton = ({
  selectedFilterBar,
  numButtons,
  switchFilterBar,
  isTaskManagerEnabled,
}) => {
  const orgId = useAppSelector((state) => state.session.currentOrganizationId);
  const { tasks, isLoading } = useFilterTasks({
    filter: 'active',
    skip: !orgId || !isTaskManagerEnabled,
    orgId,
  });
  const tasksCount = tasks.length;
  const isSelected = selectedFilterBar === 'Tasks';
  return (
    <RosterFilterBarButton
      key="tasks"
      dataTestId="tasksTab"
      isSelected={isSelected}
      onClick={switchFilterBar}
      showUnreadCount={false}
      useSelectedLine={isSelected && numButtons >= NUM_BUTTONS}
    >
      <span>Tasks</span>
      {!isLoading && !!tasksCount && !isSelected && (
        <UnreadBadgeCount
          className={classes('unread-count')}
          count={tasksCount}
          usePriorityButton={true}
          max={99}
          maxLabel={'99+'}
        />
      )}
    </RosterFilterBarButton>
  );
};

TasksTabButton.propTypes = {
  selectedFilterBar: PropTypes.string.isRequired,
  numButtons: PropTypes.number.isRequired,
  switchFilterBar: PropTypes.func.isRequired,
  isTaskManagerEnabled: PropTypes.bool.isRequired,
};

class RosterFilterBar extends Component {
  static propTypes = {
    currentOrganization: propTypes.organization,
    currentUser: propTypes.user.isRequired,
    filterUnreadCount: PropTypes.number.isRequired,
    filterUnreadPriorityCount: PropTypes.number.isRequired,
    getAdminRolesInOrganization: PropTypes.func.isRequired,
    getUnselectedUnreadCounts: PropTypes.func.isRequired,
    hideDnd: PropTypes.bool.isRequired,
    isTaskManagerEnabled: PropTypes.bool.isRequired,
    inactiveRosterUnreadCount: PropTypes.number.isRequired,
    inactiveRosterUnreadPriorityCount: PropTypes.number.isRequired,
    selectedFilter: PropTypes.string.isRequired,
    selectedFilterBar: PropTypes.string.isRequired,
    setFilterBar: PropTypes.func.isRequired,
    unreadAlertsCount: PropTypes.number.isRequired,
  };

  state = {
    menuTarget: null,
  };

  render() {
    const {
      currentOrganization,
      currentUser,
      filterUnreadCount,
      filterUnreadPriorityCount,
      getAdminRolesInOrganization,
      getUnselectedUnreadCounts,
      hideDnd,
      isTaskManagerEnabled,
      selectedFilter,
      selectedFilterBar,
      unreadAlertsCount,
    } = this.props;
    const { menuTarget } = this.state;
    const showAlerts = !!currentOrganization && currentOrganization.showAlerts;
    const showPatientCare = !!currentOrganization && currentOrganization.showPatientCare;
    const showDnd = currentUser.dnd && !hideDnd;
    const { isTeamAdmin = false } = getAdminRolesInOrganization();
    const numButtons = [
      true, // Inbox / filters
      showAlerts && selectedFilter === 'Inbox', // Alerts
      showPatientCare && selectedFilter === 'Inbox', // Care Team
      isTaskManagerEnabled && selectedFilter === 'Inbox', // Tasks
      isTeamAdmin && selectedFilter === 'Teams', // New Team +
    ].filter(Boolean).length;
    const rosterButtons = [];

    let { inactiveRosterUnreadCount, inactiveRosterUnreadPriorityCount } = this.props;
    let contextMenu,
      showPriorityButton,
      showUnreadCount,
      unreadCount,
      unreadOrArrowFragment,
      unselectedPatientCareUnreadCount,
      unselectedPatientCareUnreadPriorityCount,
      unselectedRosterUnreadCount,
      unselectedRosterUnreadPriorityCount;
    if (showAlerts && selectedFilterBar === 'Alerts') {
      const unselectedUnreadCounts = getUnselectedUnreadCounts();
      unselectedPatientCareUnreadCount = unselectedUnreadCounts.patientCareUnreadCount;
      unselectedPatientCareUnreadPriorityCount =
        unselectedUnreadCounts.patientCareUnreadPriorityCount;
      unselectedRosterUnreadCount = unselectedUnreadCounts.rosterUnreadCount;
      unselectedRosterUnreadPriorityCount = unselectedUnreadCounts.rosterUnreadPriorityCount;

      showUnreadCount = unselectedRosterUnreadCount > 0 || unselectedPatientCareUnreadCount > 0;
      showPriorityButton = unselectedRosterUnreadPriorityCount > 0;
      unreadCount = 0;
      inactiveRosterUnreadCount = 0;
      inactiveRosterUnreadPriorityCount = 0;
    } else if (showPatientCare && selectedFilterBar === 'Right') {
      showUnreadCount = inactiveRosterUnreadCount > 0;
      unreadCount = inactiveRosterUnreadCount;
      showPriorityButton = inactiveRosterUnreadPriorityCount > 0;
    } else if ((showAlerts || showPatientCare) && selectedFilter !== 'Inbox') {
      showUnreadCount = filterUnreadCount > 0 || inactiveRosterUnreadCount > 0;
      unreadCount = filterUnreadCount + inactiveRosterUnreadCount;
      showPriorityButton = filterUnreadPriorityCount > 0 || inactiveRosterUnreadPriorityCount > 0;

      if (showAlerts) {
        unreadCount += unreadAlertsCount || 0;
      }
    } else {
      showUnreadCount = filterUnreadCount > 0;
      unreadCount = filterUnreadCount;
      showPriorityButton = filterUnreadPriorityCount > 0;
    }

    if (
      (showUnreadCount && (unselectedRosterUnreadCount > 0 || unreadCount > 0)) ||
      showPriorityButton
    ) {
      unreadOrArrowFragment = (
        <UnreadBadgeCount
          className={classes('unread-count')}
          count={unselectedRosterUnreadCount || unreadCount}
          priority={showPriorityButton}
          usePriorityButton={true}
        />
      );
    } else {
      unreadOrArrowFragment = (
        <span className={classes('image-container')}>
          <DownArrowSvg aria-label="Roster Filter Bar" className={classes('image')} />
        </span>
      );
    }

    if (selectedFilterBar === 'Left') {
      contextMenu = (
        <ContextMenu
          event="click"
          offsetX={MENU_OFFSET_X}
          offsetY={MENU_OFFSET_Y}
          position="bottominnerleft"
          relativeTo={menuTarget}
          softClick={false}
          menu={<RosterFilterMenu />}
        >
          <div ref={this._setMenuTarget} className={classes('filter-container')}>
            <span>{selectedFilter}</span>
            {unreadOrArrowFragment}
          </div>
        </ContextMenu>
      );
    } else {
      contextMenu = (
        <div ref={this._setMenuTarget} className={classes('filter-container')}>
          <span>{selectedFilter}</span>
          {unreadOrArrowFragment}
        </div>
      );
    }

    rosterButtons.push(
      <RosterFilterBarButton
        key="roster"
        dataTestId="rosterTab"
        isSelected={selectedFilterBar === 'Left'}
        onClick={this._switchFilterBar('Left')}
        showUnreadCount={showUnreadCount}
        useSelectedLine={selectedFilterBar === 'Left' && numButtons >= NUM_BUTTONS}
      >
        {contextMenu}
      </RosterFilterBarButton>
    );

    if (showAlerts && selectedFilter === 'Inbox') {
      const isSelected = selectedFilterBar === 'Alerts';

      rosterButtons.push(
        <RosterFilterBarButton
          key="alerts"
          dataTestId="alertsTab"
          isSelected={isSelected}
          onClick={this._switchFilterBar('Alerts')}
          showUnreadCount={showUnreadCount}
          useSelectedLine={selectedFilterBar === 'Alerts' && numButtons >= NUM_BUTTONS}
        >
          <span>Alerts</span>
          {!isSelected && unreadAlertsCount > 0 && (
            <UnreadBadgeCount
              className={classes('unread-count')}
              count={unreadAlertsCount}
              usePriorityButton={true}
            />
          )}
        </RosterFilterBarButton>
      );
    }

    if (isTaskManagerEnabled && selectedFilter === 'Inbox') {
      rosterButtons.push(
        <TasksTabButton
          key="tasks-button"
          switchFilterBar={this._switchFilterBar('Tasks')}
          selectedFilterBar={selectedFilterBar}
          numButtons={numButtons}
          isTaskManagerEnabled={isTaskManagerEnabled}
        />
      );
    }

    if (showPatientCare && selectedFilter === 'Inbox') {
      const showPriorityButton =
        unselectedPatientCareUnreadPriorityCount > 0 || inactiveRosterUnreadPriorityCount > 0;
      let unreadFragment;

      if (
        (unselectedPatientCareUnreadCount > 0 || inactiveRosterUnreadCount > 0) &&
        selectedFilterBar !== 'Right'
      ) {
        unreadFragment = (
          <UnreadBadgeCount
            className={classes('unread-count')}
            count={unselectedPatientCareUnreadCount || inactiveRosterUnreadCount}
            priority={showPriorityButton}
            usePriorityButton={true}
          />
        );
      }
      rosterButtons.push(
        <RosterFilterBarButton
          key="care-team"
          dataTestId="careTeamTab"
          isSelected={selectedFilterBar === 'Right'}
          onClick={this._switchFilterBar('Right')}
          showUnreadCount={showUnreadCount}
          useSelectedLine={selectedFilterBar === 'Right' && numButtons >= NUM_BUTTONS}
        >
          <span>Care Team</span>
          {unreadFragment}
        </RosterFilterBarButton>
      );
    }

    return (
      <div className={classes()}>
        <div
          className={classes('filter', {
            spacingBelowFilterBar:
              (showDnd && numButtons >= NUM_BUTTONS) || selectedFilterBar === 'Alerts',
          })}
        >
          {rosterButtons}
        </div>
      </div>
    );
  }

  _setMenuTarget = (menuTarget) => {
    this.setState({ menuTarget });
  };

  _switchFilterBar = (filter) => {
    const { setFilterBar } = this.props;
    return () => setFilterBar(filter);
  };
}

export default mobxInjectSelect({
  alertStore: ['unreadAlertsCount'],
  messengerStore: ['getAdminRolesInOrganization', 'isTaskManagerEnabled'],
  rosterStore: [
    'filterUnreadCount',
    'filterUnreadPriorityCount',
    'getUnselectedUnreadCounts',
    'inactiveRosterUnreadCount',
    'inactiveRosterUnreadPriorityCount',
    'selectedFilter',
    'selectedFilterBar',
    'setFilterBar',
  ],
  sessionStore: ['currentUser'],
  userStore: ['hideDnd'],
})(RosterFilterBar);
